import React, {useCallback, useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import SwiperCore from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/swiper-react';
import 'swiper/swiper.css';
import I18n from 'app/i18n';
import {checkingPages, contentOnly, isGuest} from 'app/utils';
import {goToLinkSimpleBets} from 'app/utils/app_links';
import logEvent from 'app/amplitude/log_event';
import {setStreamParam, setStreamsTimeout} from 'app/components/streams/stream_functions';
import Stream from 'app/components/streams/stream';
import {streamStore} from 'app/store/global';
import {openAddStreamPopup} from 'app/components/streams';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {openAuth} from 'app/components/auth/auth';
import AsideItem from 'site_version/com/AsideItem';
import type StreamInterface from 'app/interfaces/StreamInterface';

const ASIDE_ICON_SIZE = '24px';
const ADD_STREAM_ICON_SIZE = 32;
const USER_CREATED_AUTHOR = 'by_user_created';
const STREAM_SLIDE_GAP = 11;

const swiperParams = {
    allowTouchMove: true,
    direction: 'horizontal' as const,
    spaceBetween: STREAM_SLIDE_GAP
};

interface RibbonOverflow {
    left: boolean
    right: boolean
}

interface StreamsAsideProps {
    compact?: boolean
}

const StreamsAside = observer(({compact}: StreamsAsideProps): React.ReactElement | null => {
    const allStreams = [...streamStore.streams, ...streamStore.userStreams] as StreamInterface[];
    const swiperRef = useRef<SwiperCore | null>(null);
    const [overflow, setOverflow] = useState<RibbonOverflow>({left: false, right: false});

    useEffect(() => {
        setStreamsTimeout();
    }, []);

    const updateOverflow = useCallback((swiper: SwiperCore): void => {
        const left = !swiper.isBeginning;
        const right = !swiper.isEnd;

        setOverflow(prev => prev.left === left && prev.right === right ? prev : {left, right});
    }, []);

    const handleSwiper = useCallback((swiper: SwiperCore): void => {
        swiperRef.current = swiper;
        updateOverflow(swiper);
    }, [updateOverflow]);

    const handleOpenStream = useCallback((stream: StreamInterface): void => {
        logEvent('STREAM_OPENED');

        const opensInNewTab = Boolean(stream && (stream.target_blank || stream.stream_url));

        if (!opensInNewTab && !checkingPages.isPlayPage()) {
            goToLinkSimpleBets();
        } else {
            setStreamParam(stream);
        }

        streamStore.chooseStream(stream);
    }, []);

    const handleStreamCreationPopup = useCallback((event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();

        if (isGuest()) {
            openAuth();
        } else {
            openAddStreamPopup();
        }
    }, []);

    const handleScrollLeft = useCallback((): void => {
        swiperRef.current?.slidePrev();
    }, []);

    const handleScrollRight = useCallback((): void => {
        swiperRef.current?.slideNext();
    }, []);

    if (contentOnly()) {
        return null;
    }

    const header = <div className="aside-item-heading__title">
        <h3>{I18n.t('stream_title')}</h3>
    </div>;
    const hostClassName = `stream-aside__ribbon-host${overflow.left ? ' stream-aside__ribbon-host--overflow-left' : ''}${
        overflow.right ? ' stream-aside__ribbon-host--overflow-right' : ''}`;

    return <AsideItem
        header={header}
        itemClassName={`stream-aside${compact ? ' stream-aside--compact' : ''}`}
        name="streams"
    >
        <div className={hostClassName}>
            <Swiper
                className="stream-aside__ribbon"
                {...swiperParams}
                slidesPerView="auto"
                onSwiper={handleSwiper}
                onSlideChange={updateOverflow}
                onResize={updateOverflow}
            >
                {allStreams.map((stream: StreamInterface) => <SwiperSlide className="stream-aside__card" key={stream.id}>
                    <Stream
                        stream={stream}
                        preview="aside"
                        iconSize={ASIDE_ICON_SIZE}
                        imageLastUpdate={streamStore.imageLastUpdate}
                        userStream={stream.author_name === USER_CREATED_AUTHOR}
                        onClick={handleOpenStream}
                    />
                </SwiperSlide>)}
                <SwiperSlide className="stream-aside__card" key="add-stream">
                    <a
                        href="#"
                        onClick={handleStreamCreationPopup}
                        className="media-preview media-preview--aside media-preview--add-stream"
                    >
                        <span title={I18n.t('add_your_own')}>
                            <SpriteIcon
                                name="#svg-stream-add"
                                size={{height: ADD_STREAM_ICON_SIZE, width: ADD_STREAM_ICON_SIZE}}
                            />
                        </span>
                        <span className="media-preview__name">
                            <span className="media-preview__name-text text-center">{I18n.t('add_your_own')}</span>
                        </span>
                    </a>
                </SwiperSlide>
            </Swiper>
            {compact && <>
                <button
                    type="button"
                    className="stream-aside__arrow stream-aside__arrow--left"
                    onClick={handleScrollLeft}
                >
                    <span className="stream-aside__arrow-glyph">‹</span>
                </button>
                <button
                    type="button"
                    className="stream-aside__arrow stream-aside__arrow--right"
                    onClick={handleScrollRight}
                >
                    <span className="stream-aside__arrow-glyph">›</span>
                </button>
            </>}
        </div>
    </AsideItem>;
});

export default StreamsAside;
